import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsQR from "jsqr";

interface TagResult {
  tag: {
    id: string;
    status: string;
    reusable: number;
    deletedAt: string | null;
  };
  fudge: {
    id: string;
    description: string;
    changeDetail: string;
    location: string | null;
    appliedByName: string;
    appliedAt: string;
  } | null;
}

type ScanState = "idle" | "requesting" | "scanning" | "loading" | "result" | "error" | "insecure";

type Availability =
  | { available: true; reusable: boolean }
  | { available: false; reason: "in-use" | "spent" | "purged" | "not-found" };

function getAvailability(result: TagResult): Availability {
  if (result.tag.deletedAt) return { available: false, reason: "purged" };
  if (result.tag.status === "unused") return { available: true, reusable: false };
  if (result.tag.status === "used" && result.tag.reusable) return { available: true, reusable: true };
  if (result.tag.status === "active") return { available: false, reason: "in-use" };
  return { available: false, reason: "spent" };
}

export default function CheckTagPage() {
  const navigate = useNavigate();
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [camError, setCamError] = useState("");
  const [tagResult, setTagResult] = useState<TagResult | null>(null);
  const [fetchError, setFetchError] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => { return () => stopCamera(); }, []);

  function stopCamera() {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function startCamera() {
    if (!window.isSecureContext) { setScanState("insecure"); return; }
    setScanState("requesting");
    setCamError("");
    setTagResult(null);
    setFetchError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play();
      setScanState("scanning");
      scan();
    } catch {
      setCamError("Camera access denied or unavailable.");
      setScanState("error");
    }
  }

  function scan() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }
    const ctx = canvas.getContext("2d")!;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
    if (code) {
      const match = code.data.match(/\/scan\/([^/?#]+)/);
      if (match) {
        stopCamera();
        checkTag(match[1]);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(scan);
  }

  async function checkTag(tagId: string) {
    setScanState("loading");
    try {
      const r = await fetch(`/api/tags/${tagId}`);
      if (!r.ok) {
        setFetchError(r.status === 404 ? "Tag not found — this QR code is not registered in the system." : "Could not contact the server.");
        setScanState("result");
        return;
      }
      const data = await r.json();
      setTagResult(data);
      setScanState("result");
    } catch {
      setFetchError("Could not contact the server.");
      setScanState("result");
    }
  }

  function fmt(iso: string) {
    return new Date(iso).toLocaleString();
  }

  const availability = tagResult ? getAvailability(tagResult) : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center pt-8 px-4 pb-8">
      <div className="w-full max-w-sm space-y-4">

        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm text-center">
          <div className="text-5xl mb-3">🔍</div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Check Tag</h1>
          <p className="text-sm text-slate-500">
            Scan a QR tag to see whether it is available to use.
          </p>
        </div>

        {/* Camera / result area */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">

          {scanState === "idle" && (
            <button onClick={startCamera}
              className="w-full py-12 flex flex-col items-center gap-3 text-slate-500 hover:bg-slate-50 transition-colors">
              <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
              </svg>
              <span className="text-sm font-medium">Tap to open camera</span>
            </button>
          )}

          {scanState === "requesting" && (
            <div className="py-12 text-center text-slate-400 text-sm">Requesting camera…</div>
          )}

          {scanState === "loading" && (
            <div className="py-12 text-center text-slate-400 text-sm">Checking tag…</div>
          )}

          {(scanState === "error" || scanState === "insecure") && (
            <div className="py-6 px-6 space-y-4">
              {scanState === "insecure" ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  In-app camera requires HTTPS. Use your phone's camera app to scan the tag instead.
                </p>
              ) : (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {camError}
                </p>
              )}
              {scanState === "error" && (
                <button onClick={startCamera} className="text-sm text-blue-600 underline">Try camera again</button>
              )}
            </div>
          )}

          {/* Live viewfinder */}
          <div className={scanState === "scanning" ? "block" : "hidden"}>
            <div className="relative">
              <video ref={videoRef} muted playsInline className="w-full rounded-xl" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 relative">
                  <span className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-md" />
                  <span className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-md" />
                  <span className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-md" />
                  <span className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-md" />
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-slate-400 py-3">Point at a QR tag…</p>
          </div>

          {/* Result */}
          {scanState === "result" && (
            <div className="p-5 space-y-4">
              {fetchError ? (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-2xl">❌</span>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Tag Not Found</p>
                    <p className="text-sm text-red-600 mt-0.5">{fetchError}</p>
                  </div>
                </div>
              ) : availability?.available ? (
                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="text-sm font-semibold text-green-800">
                      {availability.reusable ? "Available (reusable)" : "Available"}
                    </p>
                    <p className="text-sm text-green-700 mt-0.5">
                      {availability.reusable
                        ? "This tag has been used before but is set to reusable — it can be registered again."
                        : "This tag is unused and ready to register a fudge."}
                    </p>
                  </div>
                </div>
              ) : availability?.reason === "in-use" ? (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-2xl">🔴</span>
                    <div>
                      <p className="text-sm font-semibold text-red-800">Tag In Use</p>
                      <p className="text-sm text-red-600 mt-0.5">This tag is linked to an active fudge.</p>
                    </div>
                  </div>
                  {tagResult?.fudge && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1.5 text-sm">
                      <p className="font-medium text-slate-800">{tagResult.fudge.description}</p>
                      <p className="text-slate-600">{tagResult.fudge.changeDetail}</p>
                      {tagResult.fudge.location && (
                        <p className="text-slate-500 text-xs">📍 {tagResult.fudge.location}</p>
                      )}
                      <p className="text-slate-400 text-xs">
                        Applied by {tagResult.fudge.appliedByName} · {fmt(tagResult.fudge.appliedAt)}
                      </p>
                    </div>
                  )}
                </div>
              ) : availability?.reason === "spent" ? (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Tag Spent</p>
                    <p className="text-sm text-amber-700 mt-0.5">This tag has already been used and cannot be reused. Discard it and use a fresh tag.</p>
                  </div>
                </div>
              ) : availability?.reason === "purged" ? (
                <div className="flex items-start gap-3 p-4 bg-slate-100 border border-slate-200 rounded-lg">
                  <span className="text-2xl">🗑️</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Tag Purged</p>
                    <p className="text-sm text-slate-500 mt-0.5">This tag has been removed from the system by an administrator.</p>
                  </div>
                </div>
              ) : null}

              <button
                onClick={startCamera}
                className="w-full py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Check Another Tag
              </button>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <button
          onClick={() => { stopCamera(); navigate("/dashboard"); }}
          className="w-full py-3 text-sm text-slate-500 border border-slate-200 rounded-xl bg-white hover:bg-slate-50"
        >
          Cancel — go to dashboard
        </button>
      </div>
    </div>
  );
}
